import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from '~/components/HeaderNotification';
import { Settings, Save } from 'lucide-react';
import Select from 'react-select';
import { searchIndex } from '~/layouts/components/LeftSideBarAdmin/searchIndex';

const apiUrl = process.env.REACT_APP_API_URL;

const groupedOptions = [];
let currentGroup = null;

searchIndex.forEach(route => {
    if (route.subtext === "Menu" || route.subtext === "Home") {
        currentGroup = {
            label: route.text,
            options: []
        };
        groupedOptions.push(currentGroup);

        if (route.to && route.to !== "/ai-chatbot/administrator") {
            currentGroup.options.push({
                value: route.to,
                label: `Overview (Base Route)`,
                routeData: { Name: route.text, Path: route.to }
            });
        }
    } else if (route.to) {
        if (!currentGroup) {
            currentGroup = { label: "Other", options: [] };
            groupedOptions.push(currentGroup);
        }
        currentGroup.options.push({
            value: route.to,
            label: route.text,
            routeData: { Name: route.text, Path: route.to }
        });
    }
});

// Create a flat map to quickly look up existing options when rendering values
const flatOptionsMap = new Map();
groupedOptions.forEach(g => g.options.forEach(o => flatOptionsMap.set(o.value, o)));

const SearchSettingsModal = ({ show, onClose }) => {
    const [tables, setTables] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (show) {
            fetchSettings();
            setError(null);
        }
    }, [show]);

    const fetchSettings = async () => {
        setIsLoading(true);
        try {
            const res = await axios.get(`${apiUrl}/api/admin/global-search/settings`, { withCredentials: true });
            if (res.data?.Succeeded) {
                const data = res.data.Data || [];
                setTables(data.map(t => ({ ...t, Routes: t.Routes || [] })));
            }
        } catch (error) {
            console.error("Error fetching search settings", error);
            toast.error("Failed to load search settings");
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        setError(null);
        setIsSaving(true);
        try {
            // Only send selected tables to keep JSON small and avoid hitting DB NVARCHAR(1000) limit
            const tablesToSave = tables.filter(t => t.IsSelected);
            const res = await axios.post(`${apiUrl}/api/admin/global-search/update-settings`, 
                tablesToSave,
                { 
                    headers: { 'Content-Type': 'application/json' },
                    withCredentials: true 
                }
            );

            if (res.data?.Succeeded) {
                toast.success("Search settings updated successfully");
                onClose();
            } else {
                setError(res.data?.Message || "Failed to update settings");
            }
        } catch (err) {
            console.error("Error saving search settings", err);
            setError("An error occurred while saving");
        } finally {
            setIsSaving(false);
        }
    };

    const handleToggleTable = (tableName) => {
        setTables(tables.map(t => {
            if (t.TableName === tableName) {
                return { ...t, IsSelected: !t.IsSelected };
            }
            return t;
        }));
    };

    const handleRoutesChange = (tableName, selectedOptions) => {
        setTables(tables.map(t => {
            if (t.TableName === tableName) {
                return { ...t, Routes: selectedOptions.map(opt => opt.routeData) };
            }
            return t;
        }));
    };

    if (!show) return null;

    return (
        <>
            <div className="modal-backdrop fade show" style={{ zIndex: 1050 }}></div>
            <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1051, backgroundColor: 'rgba(0,0,0,0.5)' }}>
                <div className="modal-dialog modal-dialog-centered modal-lg">
                    <div className="modal-content border-0 shadow rounded-4">
                        <div className="modal-header border-bottom px-4 py-3 bg-light rounded-top-4">
                            <h5 className="modal-title fw-bold text-body-emphasis d-flex align-items-center">
                                <Settings size={20} className="me-2 text-primary" /> Search Settings
                            </h5>
                            <button type="button" className="btn-close" onClick={onClose}></button>
                        </div>
                        <div className="modal-body p-4">
                            {isLoading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="row g-4">
                                    {error && (
                                        <div className="col-12 mb-0 pb-0">
                                            <div className="alert alert-danger py-2 px-3 mb-0 d-flex align-items-center fs-9 fw-bold">
                                                {error}
                                            </div>
                                        </div>
                                    )}

                                    <div className="col-12">
                                        <label className="form-label fw-bold text-body-emphasis">Global Search Tables</label>
                                        <p className="text-body-tertiary fs-9 mb-2">
                                            Select the exact database tables that are allowed to be searched globally, and assign the corresponding Admin Routes (Tabs) for navigation.
                                        </p>
                                        <div className="border rounded-2 p-0 bg-white" style={{ maxHeight: '450px', overflowY: 'auto', overflowX: 'hidden' }}>
                                            <table className="table table-hover align-middle mb-0">
                                                <thead className="table-light sticky-top" style={{ zIndex: 10 }}>
                                                    <tr>
                                                        <th className="border-bottom-0 py-2 ps-3" style={{ width: '40px' }}>Enable</th>
                                                        <th className="border-bottom-0 py-2" style={{ width: '150px' }}>Table Name</th>
                                                        <th className="border-bottom-0 py-2 pe-3">Assigned Routes (Multi-Select)</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {tables.map((table, idx) => {
                                                        const isSelected = table.IsSelected;
                                                        
                                                        // Map stored route data back to react-select options
                                                        const selectedValues = (table.Routes || []).map(r => {
                                                            const existingOpt = flatOptionsMap.get(r.Path);
                                                            return existingOpt || { value: r.Path, label: r.Name, routeData: r };
                                                        });

                                                        return (
                                                            <tr key={idx} className={isSelected ? 'bg-primary bg-opacity-10' : ''}>
                                                                <td className="ps-3 text-center">
                                                                    <input 
                                                                        className="form-check-input m-0 cursor-pointer border-secondary" 
                                                                        type="checkbox" 
                                                                        checked={isSelected}
                                                                        onChange={() => handleToggleTable(table.TableName)}
                                                                        style={{ cursor: 'pointer' }}
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <label className="text-body-secondary fs-9 mb-0 text-truncate fw-bold">
                                                                        {table.TableName}
                                                                    </label>
                                                                </td>
                                                                <td className="pe-3 py-2">
                                                                    <Select
                                                                        isMulti
                                                                        options={groupedOptions}
                                                                        value={selectedValues}
                                                                        onChange={(selected) => handleRoutesChange(table.TableName, selected || [])}
                                                                        isDisabled={!isSelected}
                                                                        placeholder="Select routes & tabs..."
                                                                        classNamePrefix="react-select"
                                                                        menuPortalTarget={document.body}
                                                                        components={{ IndicatorSeparator: () => null }}
                                                                        styles={{
                                                                            menuPortal: base => ({ ...base, zIndex: 9999 }),
                                                                            control: (base) => ({
                                                                                ...base,
                                                                                fontSize: '0.875rem',
                                                                                minHeight: '32px'
                                                                            }),
                                                                            option: (base) => ({
                                                                                ...base,
                                                                                fontSize: '0.875rem'
                                                                            })
                                                                        }}
                                                                    />
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer border-top bg-light p-3 justify-content-end rounded-bottom-4">
                            <button type="button" className="btn btn-sm btn-light fw-bold px-3 text-body-secondary border" onClick={onClose} disabled={isSaving}>
                                Cancel
                            </button>
                            <button type="button" className="btn btn-sm btn-primary fw-bold px-4 d-flex align-items-center" onClick={handleSave} disabled={isSaving || isLoading}>
                                {isSaving ? <span className="spinner-border spinner-border-sm me-2" /> : <Save size={16} className="me-2" />}
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SearchSettingsModal;
